import React, { useState, useEffect } from "react";

import PropTypes from "prop-types";
import { visuallyHidden } from "@mui/utils";

import Paper from "@mui/material/Paper";
import { makeStyles } from "tss-react/mui";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import OverflowTip from "../../Common/OverflowTip";
import OverflowDiv from "../../DataCatalog/OverflowDiv";
import CopyButton from "../CopyButton";

function DataTableHead(props) {
  const { columns, order, orderBy, onRequestSort, hidden, classes } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead style={hidden ? { visibility: "collapse" } : {}}>
      <TableRow>
        {columns.map((headCell, index) => {
          return (
            <TableCell
              key={index}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={order}
              style={{
                width: headCell.width,
              }}
              className={classes.TableHead}
            >
              {!headCell.sortable ? (
                headCell.label
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

DataTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles()((theme) => ({
  DataTableContainer: {
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    padding: "0px",
    overflowX: "auto",
    "& .MuiTableContainer-root": {
      borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "& .MuiTablePagination-toolbar .MuiTablePagination-selectLabel, .MuiTablePagination-toolbar .MuiTablePagination-displayedRows":
      {
        display: "flex",
        alignSelf: "center",
        marginBottom: 0,
      },
  },
  TableHead: {
    fontWeight: "bold",
    "& .MuiTableCell-root": {
      color: "rgba(0, 0, 0, 0.87)",
      padding: "6px 16px",
      height: "37px",
    },
  },
  TableBody: {
    "& .MuiOutlinedInput-input": {
      padding: "4px 10px",
    },
    "& .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
  },
  TableRow: {
    // Remove border from body's rows
    "&:last-child td": {
      border: 0,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
  IconHolder: {
    borderRadius: "50px",
    margin: "0",
    padding: "5px 5px",
    fontSize: "19px",
    "&.Mui-disabled": {
      color: "#00000042 !important",
    },
  },
}));

const TableWithPaginationSorting = (props) => {
  const {
    defaultOrder,
    defaultOrderBy,
    options,
    usePagination,
    setOptions,
    handleRequest,
    tableMaxHeight,
    tableMaxWidth,
    columns,
    tableData,
    RowClick,
    rowHover,
    loading,
    rowHeight,
    ellipsisColumns,
    showCopy,
    textMaxLength,
    handleTextChange,
    handleEditClick,
    handleDeleteClick,
    noDataColspan,
    noDataMessage,
    rowsPerPageOptions,
    count,
    selection,
    handleInfo,
    elevation = 1,
  } = props;

  const {
    order: optionOrder = defaultOrder,
    orderBy: optionOrderBy = defaultOrderBy,
    page: optionPage = 0,
    limit: optionLimit = 10,
  } = options;

  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);
  const { classes } = useStyles();

  const handleRequestSort = (event, property) => {
    if (loading) return;
    const isAsc = orderBy === property && order === "asc";
    let tmpOrder = isAsc ? "desc" : "asc";

    setOrder(tmpOrder);
    setOrderBy(property);

    let updatedOptions = {
      ...options,
      orderBy: property,
      order: tmpOrder,
      ...(usePagination !== false ? { limit, page } : {}),
    };

    setOptions(updatedOptions);
    handleRequest(updatedOptions);
  };

  const handleChangePage = (_event, newPage) => {
    if (loading) return;
    setPage(newPage);
    let updatedOptions = {
      ...options,
      page: newPage,
    };
    setOptions(updatedOptions);
    handleRequest(updatedOptions);
  };

  const handleChangeRowsPerPage = (event) => {
    if (loading) return;
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
    let updatedOptions = {
      ...options,
      limit: parseInt(event.target.value, 10),
      page: 0,
    };
    setOptions(updatedOptions);
    handleRequest(updatedOptions);
  };

  useEffect(() => {
    if (optionPage !== page) setPage(optionPage);
    if (optionLimit !== limit) setLimit(optionLimit);
    if (optionOrder !== order) setOrder(optionOrder);
    if (optionOrderBy !== orderBy) setOrderBy(optionOrderBy);
  }, [page, limit, order, orderBy, optionPage, optionOrder, optionOrderBy, optionLimit]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer} elevation={elevation}>
      <TableContainer
        sx={{
          maxHeight: tableMaxHeight || "none",
          maxWidth: tableMaxWidth || "100%",
          flexGrow: 1,
        }}
      >
        <Table stickyHeader aria-label="sticky table" size="small">
          <DataTableHead
            columns={columns}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={tableData.length}
            classes={classes}
          />
          <TableBody className={classes.TableBody}>
            {tableData.length && !loading ? (
              tableData.map((row, key) => (
                <TableRow
                  key={key}
                  onClick={() => {
                    const selectedRowID = row?.[0]?.RowId;
                    setSelectedRow(selectedRowID);
                    if (typeof RowClick === "function") RowClick(row, selectedRowID);
                  }}
                  sx={{ cursor: rowHover ? "pointer" : "text", height: rowHeight || 33 }}
                  hover={rowHover || false}
                  className={classes.TableRow}
                  selected={selection && row?.[0]?.RowId === selectedRow}
                >
                  {row.map((cell, index) =>
                    ellipsisColumns?.find((idx) => idx === index + 1) ? (
                      <Box
                        component={TableCell}
                        key={key + index}
                        sx={{ width: columns[index]?.width, minWidth: "120px", maxWidth: "250px" }}
                      >
                        {cell.showInfo && (
                          <Tooltip arrow title="View Files">
                            <IconButton
                              size="small"
                              color={cell.infoColor}
                              sx={{ mr: "5px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(cell?.RowId);
                                if (typeof handleInfo === "function") handleInfo(row, cell?.RowId, index);
                              }}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <OverflowTip
                          title=<Typography variant="body2">{cell.value ?? ""}</Typography>
                          arrow
                          placement="left"
                        >
                          <OverflowDiv
                            cellContent={cell.value ?? ""}
                            width={`calc(100% - ${showCopy ? "30px" : "0px"} - ${
                              cell.showInfo ? "30px" : "0px"
                            } - 20px) !important`}
                            align={cell.align || "inherit"}
                            color={cell.color ?? "black"}
                            cellStyle={{ display: "inline-block" }}
                          />
                        </OverflowTip>
                        {cell.message && showCopy && <CopyButton value={cell.message} />}
                      </Box>
                    ) : (
                      <TableCell
                        component="td"
                        key={key + index}
                        scope="row"
                        align={cell.align || "inherit"}
                        sx={{
                          width: columns[index].width ? `${columns[index].width} !important` : "",
                          color: cell.color ? cell.color : "black",
                        }}
                      >
                        {cell.editable ? (
                          <TextField
                            inputProps={textMaxLength ? { maxLength: textMaxLength } : ""}
                            value={cell.value || ""}
                            sx={cell.textWidth ? { width: cell.textWidth } : ""}
                            name={`${columns[index].name}[]`}
                            onChange={(e) => handleTextChange(e, key, index)}
                          />
                        ) : cell.value ? (
                          cell.value
                        ) : cell.editIcon && cell.deleteIcon ? (
                          <>
                            <IconButton
                              className={classes.IconHolder}
                              color="error"
                              onClick={() => handleEditClick(key)}
                              disabled={cell.disabled}
                            ></IconButton>
                            <IconButton
                              className={classes.IconHolder}
                              style={{ marginLeft: "5px" }}
                              color="error"
                              onClick={() => handleDeleteClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.deleteIcon}
                            </IconButton>
                          </>
                        ) : (
                          (cell.editIcon ? (
                            <IconButton
                              className={classes.IconHolder}
                              color="error"
                              onClick={() => handleEditClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.editIcon}
                            </IconButton>
                          ) : (
                            ""
                          ),
                          cell.deleteIcon ? (
                            <IconButton
                              className={classes.IconHolder}
                              style={{ marginLeft: "5px" }}
                              color="error"
                              onClick={() => handleDeleteClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.deleteIcon}
                            </IconButton>
                          ) : (
                            ""
                          ))
                        )}
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow className={classes.TableRow} sx={{ height: rowHeight || 33 }}>
                <TableCell colSpan={noDataColspan} component="td" scope="row" align={"center"}>
                  {loading ? <CircularProgress color="primary" size={33} /> : noDataMessage || "No data available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {usePagination !== false ? (
        <TablePagination
          className={classes.pagination}
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={optionLimit}
          page={optionPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </Paper>
  );
};

export default TableWithPaginationSorting;
